import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

export function PasswordInput({ value, onChange, placeholder = "Create a strong password", required, className = "form-control", minLength, autoComplete = "new-password", name, readOnly, onFocus }) {
  const [show, setShow] = useState(false);
  return (
    <div className="position-relative w-100" style={{ overflow: "visible" }}>
      <input
        type={show ? "text" : "password"}
        required={required}
        minLength={minLength}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={className}
        autoComplete={autoComplete}
        name={name}
        readOnly={readOnly}
        onFocus={onFocus}
        style={{ paddingRight: 44 }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-secondary p-0 me-2 border-0"
        title={show ? "Hide password" : "Show password"}
        aria-label={show ? "Hide password" : "Show password"}
        style={{ zIndex: 6, lineHeight: 1 }}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

export function Field({ label, children, theme }) {
  return (
    <div className="mb-3">
      <label 
        className="form-label small fw-bold text-uppercase mb-1" 
        style={{ 
          color: theme?.textMute || "#64748b",
          fontSize: "11px",
          letterSpacing: "0.5px",
          display: "block"
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export function inputStyle(theme) {
  return {
    width: "100%",
    padding: "10px 14px",
    borderRadius: "8px",
    border: `1px solid ${theme?.line || "#cbd5e1"}`,
    backgroundColor: theme?.surface || "#ffffff",
    color: theme?.text || "#0f172a",
    fontSize: "13.5px",
    fontFamily: "inherit",
    boxSizing: "border-box",
    outline: "none",
    transition: "all 0.2s ease-in-out"
  };
}

export function Select({ value, onChange, options = [], className = "", required, theme, style }) {
  const isDark = theme?.key === "dark";
  const arrowColor = isDark ? "%23ffffff" : "%230b1b3d";

  return (
    <select
      required={required}
      value={value}
      onChange={onChange}
      className={`form-select ${className}`}
      style={{
        ...inputStyle(theme),
        cursor: "pointer",
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='${arrowColor}' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`,
        backgroundPosition: "right 14px center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "14px 10px",
        paddingRight: "38px",
        ...style
      }}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled} style={{ backgroundColor: theme?.surface || "#ffffff", color: theme?.text || "#0f172a" }}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function Button({ children, onClick, variant = "primary", className = "", type = "button", disabled, style, theme }) {
  const btnClass = variant === "ghost" ? "btn btn-outline-secondary" : `btn btn-${variant}`;
  const customStyle = theme ? {
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "13.5px",
    padding: "10px 18px",
    transition: "all 0.2s ease-in-out",
    boxShadow: "0 2px 6px rgba(2, 132, 199, 0.2)",
    ...style
  } : style;

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${btnClass} ${className} d-inline-flex align-items-center gap-2`} style={customStyle}>
      {children}
    </button>
  );
}

export function Slider({ label, icon: Icon, value, onChange }) {
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="small text-body d-flex align-items-center gap-1">
          {Icon && <Icon size={14} className="text-secondary" />} {label}
        </span>
        <span className="badge bg-primary fs-6">{value}/10</span>
      </div>
      <input type="range" className="form-range" min="1" max="10" value={value} onChange={e => onChange(Number(e.target.value))} />
    </div>
  );
}

export function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">
      {message}
    </div>
  );
}
